/**
 * Minutes from midnight, as the appointment tables hold them and as a clock shows them.
 *
 * `appointment_schedule.opens_at_minute`, its `closes_at_minute`, `appointment_hours_override` and
 * `appointment_weekday_hours` are all `smallint` minutes from midnight, and every screen that edits
 * one binds it to `<input type="time">`, which speaks `HH:MM`. Two screens had their own identical
 * copy of this pair and a third was about to make it three.
 *
 * **Why `1440` is not expressible and is kept anyway.** The column allows 1440, the end of the day,
 * and `<input type="time">` has no way to show it: 24:00 is not a valid value and 00:00 is the
 * start of the day, eighteen hours earlier than a shop that closes at six. So `timeOf` clamps to
 * 23:59 for display, and the two screens keep a stored 1440 unchanged unless the closing time is
 * edited. That is not cosmetic: `availableSlots` offers a start only while
 * `start + duration <= closesAtMinute`, so writing 1439 back silently loses the last slot of every
 * day.
 */

/** The end of the day, and the one value `timeOf` cannot show. */
export const DAY_MINUTES = 1440

/**
 * Minutes from midnight as `HH:MM`, clamped to 23:59.
 *
 * The clamp is the midnight case above, and it is a clamp rather than a refusal because this is a
 * display function: a form that cannot render its own stored value is worse than one showing the
 * last minute of the day.
 */
export const timeOf = (minute: number): string => {
  const m = Math.min(minute, DAY_MINUTES - 1)
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

/**
 * `HH:MM` as minutes from midnight, or `null` where it is not a time.
 *
 * Null rather than `NaN` or 0, because 0 is a real opening minute: a branch that opens at midnight
 * is minute 0, so no caller can use a falsy check to mean "not a time".
 */
export const minuteOf = (time: string): number | null => {
  const match = /^(\d{2}):(\d{2})$/.exec(time)
  if (match === null) return null
  return Number(match[1]) * 60 + Number(match[2])
}
